import {StyleSheet} from "aphrodite";
import * as React from "react";
// @ts-expect-error - TS2724 - '"react-router-dom"' has no exported member named 'Match'. Did you mean 'match'? | TS2305 - Module '"react-router-dom"' has no exported member 'Location'.
import {Route, type Location, type Match} from "react-router-dom";

import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelMedium} from "@khanacademy/wonder-blocks-typography";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedClientLink from "../../client-link-package/deprecated-client-link.tsx";

import {NavSubsectionContext} from "./nav-subsection.tsx";

type Props = {
    href: string;
    label: React.ReactNode;
    handleIsCurrentPage?: (match: Match, location: Location) => boolean;
    onClick?: () => unknown;
    testId?: string;
};

// These extra props are provided automatically by the container component.
type PropsForStatelessComponent = Props & {
    // Whether this link points to the current page.
    isCurrentPage: boolean;
    // Whether this link is inside a <NavSubsection />.
    isInSubsection: boolean;
};

const BORDER_SIZE = 1;

/**
 * The core NavLink component. A simple wrapper for Wonder Blocks <Link />,
 * plus some styles and behaviors to fit the nav tree layout.
 *
 * This is actually the _stateless_ version of the component: rather than
 * detecting the current route in here, the <NavLink /> component provides an
 * `isCurrentPage` prop. This enables us to write fixture tests for the visuals
 * of this component, and check them separately from the routing integration.
 * (It also helps us avoid excess indentation from render-prop components!)
 */
export function StatelessNavLink({
    href,
    label,
    isCurrentPage,
    isInSubsection,
    testId,
    onClick,
}: PropsForStatelessComponent): React.ReactElement<React.ComponentProps<"li">> {
    // We are using ClientLink below instead of WonderBlocks Link since we need
    // to customize the styles for this navigation
    return (
        <li>
            <DeprecatedClientLink
                aria-current={
                    isCurrentPage ? ("page" as unknown as boolean) : false
                }
                to={href}
                testId={testId}
                onClick={onClick}
                style={[
                    styles.navLink,
                    isCurrentPage && styles.navLinkForCurrentPage,
                    isInSubsection && styles.navLinkInSubsection,
                ]}
            >
                {isCurrentPage ? (
                    <LabelLarge>{label}</LabelLarge>
                ) : (
                    <LabelMedium>{label}</LabelMedium>
                )}
            </DeprecatedClientLink>
        </li>
    );
}

/**
 * The NavLink container component. This wrapper provides stateful information
 * like `isCurrentPage` to the StatelessNavLink.
 *
 * TODO(mdr): Soon we'll also use context to provide the hierarchy level! Links
 *     inside a <NavSubsection /> should not be bold.
 */
function NavLink(props: Props): React.ReactElement {
    const {handleIsCurrentPage} = props;
    const isCurrentPage = (match: Match, location: Location) => {
        if (handleIsCurrentPage != null) {
            return handleIsCurrentPage(match, location);
        }
        return !!match;
    };

    return (
        <NavSubsectionContext.Consumer>
            {({isInSubsection}) => (
                <Route path={props.href}>
                    {({match, location}) => (
                        <StatelessNavLink
                            {...props}
                            isCurrentPage={isCurrentPage(match, location)}
                            isInSubsection={isInSubsection}
                        />
                    )}
                </Route>
            )}
        </NavSubsectionContext.Consumer>
    );
}

const styles = StyleSheet.create({
    navLink: {
        color: color.offBlack,
        borderRadius: 4,
        paddingTop: spacing.small_12 - BORDER_SIZE,
        paddingBottom: spacing.small_12 - BORDER_SIZE,
        paddingLeft: spacing.large_24,
        border: `${BORDER_SIZE}px solid transparent`,
        display: "block",
        cursor: "pointer",
        ":focus": {
            borderColor: color.blue,
            outline: 0,
        },
        ":hover": {
            textDecoration: "none",
            color: color.offBlack64,
        },
    },
    navLinkForCurrentPage: {
        backgroundColor: color.offWhite,
        color: color.blue,
        ":hover": {
            color: color.blue,
        },
    },
    navLinkInSubsection: {
        paddingLeft: spacing.xLarge_32,
    },
});

export default NavLink;
